import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { DatasetBrowserDataResponseType, DatasetsBrowserDataResponse } from './dataset-response.types';
import { mapToDatasetBrowserData } from './dataset-browser-data-mappers';
import { getInitialFlagValue, MetaTags, MetaTagsService } from '@mode/shared/util-js';
import {
  ExportedDatasetSourceContent,
  ExportedDatasetSourceResponseType,
  FeatureFlag,
  ProducerType,
  ProducerTypes,
  PublishedDatasetResource,
  ThoughtspotDatasetPublicationResponse,
} from '@mode/shared/contract-common';
import { getHref } from '@mode/shared/util-halogen';
import { ModeHalResource } from '@mode/shared/contract-common';

@Injectable({
  providedIn: 'root',
})
export class DatasetClientService {
  enableDatasetsPagination = getInitialFlagValue(FeatureFlag.ShowDatasetsPagination);

  constructor(
    private http: HttpClient,
    private metaTags: MetaTagsService
  ) {}

  public deleteDataset(datasetToken: string): Observable<Object> {
    return this.http.delete(`/api/${this.metaTags.getTag(MetaTags.OwnerUsername)}/datasets/${datasetToken}`);
  }

  public getImportableDatasetsBrowserData(
    org: string,
    datasetType: string,
    page: number,
    search: string
  ): Observable<DatasetsBrowserDataResponse> {
    const properties = [
      'name',
      'description',
      'collection_name',
      'last_successfully_run_at',
      'token',
      'switch_view_token',
      'is_sample',
    ].toString();
    const params: { [key: string]: string | string[] } = {
      'embed[reports][only]': properties,
      'embed[reports][creator][name]': '1',
    };
    const url = this.enableDatasetsPagination
      ? `/api/${org}/dataset_reports_in_space?filter=${datasetType}&page=${page}&per_page=50&search=${search}`
      : `/api/${org}/datasets?filter=exclude_personal&search=${search}`;
    return this.http
      .get<DatasetBrowserDataResponseType>(url, {
        params,
      })
      .pipe(map((responses) => mapToDatasetBrowserData(responses)));
  }

  public publishDataset(
    datasetToken: string,
    name: string,
    producerType: ProducerType,
    queryToken?: string
  ): Observable<ThoughtspotDatasetPublicationResponse> {
    const orgName = this.metaTags.getTag(MetaTags.OwnerUsername);
    const url =
      producerType === ProducerTypes.NotebookCell
        ? `/api/${orgName}/reports/${datasetToken}/datasets/${queryToken}/publish`
        : `/api/${orgName}/datasets/${datasetToken}/publish`;
    return this.http
      .post<PublishedDatasetResource>(`${url}?embed[thoughtspot_publisher]=1`, {
        name,
      })
      .pipe(
        map((publishedDataset) => ({
          publishedAt: publishedDataset?.thoughtspot_published_at,
          publisher: publishedDataset?._embedded?.thoughtspot_publisher?.name,
          publisherUsername: publishedDataset?._embedded?.thoughtspot_publisher?.username,
          publishUrl: publishedDataset?.thoughtspot_publish_url,
          publicationState: publishedDataset?.thoughtspot_publication_state,
          datasetThoughtspotPublicationToken: publishedDataset?.dataset_thoughtspot_publication_token,
        }))
      );
  }

  public republishDataset(datasetThoughtspotPublicationToken: string) {
    const orgName = this.metaTags.getTag(MetaTags.OwnerUsername);
    const url = `/api/${orgName}/dataset_thoughtspot_publications/${datasetThoughtspotPublicationToken}/republish`;
    return this.http.put(url, {});
  }

  public unpublishDataset(
    datasetToken: string,
    producerType: ProducerType,
    datasetThoughtspotPublicationToken: string
  ) {
    const orgName = this.metaTags.getTag(MetaTags.OwnerUsername);
    if (producerType === ProducerTypes.NotebookCell) {
      return this.http.delete(`/api/${orgName}/dataset_thoughtspot_publications/${datasetThoughtspotPublicationToken}`);
    } else {
      return this.http.post(`/api/${orgName}/datasets/${datasetToken}/unpublish`, {});
    }
  }

  public fetchSourceForExportedDataset(res: ModeHalResource): Observable<ExportedDatasetSourceContent> {
    const url = getHref(res['resource'], 'query_source_cell_content') || '';
    return this.http.get<ExportedDatasetSourceResponseType>(url).pipe(
      map((response) => ({
        cellContent: response.cell_content,
        executionCount: response.execution_count,
      }))
    );
  }
}
